import { Button } from '@econsult/econsult-ui/dist/components/Button';
import { HiScribeCopilotLogo } from 'assets';
import animationData from 'assets/lottie/loading_hi_scribe_2.json';
import Lottie from 'lottie-react';
import { SAVING } from 'model/toolbar/saveToRecord';
import { StyledPaper } from 'pages/Summary/styles';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  clearCopilotResponse,
  clearLetterSummary,
  fetchCopilotQuery,
  generateCopilotSummary,
  PromptType,
  resetCopilotQueryResult,
  updateMessage,
} from 'store/action/copilot';
import { updateSummary } from 'store/action/summary';
import { useAppDispatch, useAppState } from 'store/configureStore';
import { ResponseModal } from './responseModal';
import {
  CharacterCounter,
  Content,
  CopilotContainer,
  FakePlaceholder,
  LogoContainer,
  MaxCharacterContainerRow,
  MaxCharMessage,
  MaxCharMessageWrapper,
  PromptSupportCopyText,
  Textarea,
  TextAreaWrapper,
  TextAreaWrapperInner,
} from './styles';
import './styles.css';
import { SummaryModal } from './SummaryModal';

const maxChars = 500;
const placeHolderText = 'Ask Huma intelligence to edit the summary...';
const promptCopy = 'Please do not include patient identifiable information in your prompt.';

export const Copilot = () => {
  const {
    featureFlags: { SUMMARISER_REFERRAL_BUTTON_ENABLED, SUMMARISER_COPILOT_ENABLED },
    toolbar: { saveToRecordStatus },
    copilot: { queryMessage, isQueryRequestSent, response: copilotQueryResponse, letterSummary },
    summary: { currentSummary, consultationId },
    app: { isEditingSummary, selectedPractice },
  } = useAppState();
  const [showEditResponseModal, setShowEditResponseModal] = useState<boolean>(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [editedCopilotResponse, setEditedCopilotResponse] = useState<string>(copilotQueryResponse);
  const [summaryModalTitle, setSummaryModalTitle] = useState('');
  const [summaryModalContent, setSummaryModalContent] = useState('');
  const [summaryModalPromptType, setSummaryModalPromptType] = useState<PromptType | null>(null);
  const [isLetterVersion, setIsLetterVersion] = useState<boolean>(false);
  const location = useLocation();
  const transcription = Array.isArray(location.state?.transcript) ? location.state.transcript.join(' ') : '';
  const dispatch = useAppDispatch();
  const messageInputRef = useRef(null);
  const selectedPracticeId = selectedPractice?.id;

  useEffect(() => {
    if (copilotQueryResponse?.length) {
      setEditedCopilotResponse(copilotQueryResponse);
    }
  }, [copilotQueryResponse, letterSummary]);

  if (!SUMMARISER_COPILOT_ENABLED) {
    return null;
  }

  const handleUpdateMessage = (updatedMessage: string) => dispatch(updateMessage(updatedMessage));
  const queryPayload = {
    consultationId: consultationId,
    transcription: transcription,
    summary: currentSummary,
    query: queryMessage,
  };

  const handleClickAskCopilotButton = async () => {
    if (selectedPracticeId) {
      const res = await fetchCopilotQuery(dispatch, queryPayload, selectedPracticeId);
      if (res?.statusCode === 200) {
        setShowEditResponseModal(true);
      }
    }
  };

  const handleClickGenerateSummary = async (promptType: PromptType, title: string) => {
    if (selectedPracticeId) {
      const summary = await generateCopilotSummary(currentSummary, selectedPracticeId, dispatch, promptType, false);
      setSummaryModalContent(summary || 'No summary generated');
      setSummaryModalTitle(title);
      setSummaryModalPromptType(promptType);
      setIsLetterVersion(false);
      setShowSummaryModal(true);
    }
  };

  const charsOverBy = (queryMessage.length ?? 0) - maxChars;
  const maxCharsExceeded = queryMessage.length > maxChars;
  const isDisabled =
    isQueryRequestSent ||
    maxCharsExceeded ||
    queryMessage.length === 0 ||
    isEditingSummary ||
    saveToRecordStatus === SAVING;

  const isReferralInSummary = currentSummary.toLowerCase().includes('referral');
  const referralButtonText = isReferralInSummary
    ? 'Generate referral summary (Recommended)'
    : 'Generate referral summary';
  const shouldShowReferralButton =
    currentSummary && currentSummary.trim().length > 0 && !isEditingSummary && SUMMARISER_REFERRAL_BUTTON_ENABLED;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && queryMessage.length > 0) {
      e.preventDefault(); // Prevent newline
      handleClickAskCopilotButton();
    }
  };

  const handleReplaceSummary = () => {
    dispatch(updateSummary(editedCopilotResponse));
    dispatch(clearCopilotResponse());
    setShowEditResponseModal(false);
  };

  const handleCloseModal = () => {
    dispatch(resetCopilotQueryResult());
    setShowEditResponseModal(false);
  };

  const handleCloseSummaryModal = () => {
    dispatch(clearLetterSummary());
    dispatch(clearCopilotResponse());
    setEditedCopilotResponse('');
    setShowSummaryModal(false);
    setSummaryModalContent('');
    setSummaryModalTitle('');
    setSummaryModalPromptType(null);
    setIsLetterVersion(false);
  };

  const handleEditCopilotResponse = (editedResponse: string) => {
    setEditedCopilotResponse(editedResponse);
  };

  const handleToggleLetterVersion = async () => {
    const newIsLetterVersion = !isLetterVersion;
    setIsLetterVersion(newIsLetterVersion);

    if (newIsLetterVersion && !letterSummary && selectedPracticeId) {
      // Fetch letter version if not already available
      const fetchedSummary = await generateCopilotSummary(
        currentSummary,
        selectedPracticeId,
        dispatch,
        summaryModalPromptType as PromptType,
        true,
      );
      // Update modal content based on the new letter version state
      setSummaryModalContent(fetchedSummary || 'No letter summary generated');
    } else {
      // Update modal content based on the new letter version state
      setSummaryModalContent(
        newIsLetterVersion
          ? letterSummary || 'No letter summary generated'
          : copilotQueryResponse || 'No summary generated',
      );
    }
  };
  return (
    <StyledPaper style={{ marginTop: '12px', padding: '0 8px 8px 8px' }} data-automation="CopilotPaper">
      <ResponseModal
        showResponseModal={showEditResponseModal}
        handleCloseModal={handleCloseModal}
        copilotQueryResponse={editedCopilotResponse || copilotQueryResponse}
        handleReplaceSummary={handleReplaceSummary}
        handleEditCopilotResponse={handleEditCopilotResponse}
        data-automation="CopilotResponseModal"
      />
      <SummaryModal
        showModal={showSummaryModal}
        handleCloseModal={handleCloseSummaryModal}
        summary={summaryModalContent}
        title={summaryModalTitle}
        isLetterVersion={isLetterVersion}
        onToggleLetterVersion={handleToggleLetterVersion}
        data-automation="CopilotSummaryResponseModal"
      />
      <CopilotContainer data-automation="CopilotContainer">
        <LogoContainer
          data-automation="LogoContainer"
          className={`rotating-border ${isQueryRequestSent ? 'rotate-animation' : ''}`}
        >
          {!isQueryRequestSent && <HiScribeCopilotLogo style={{ height: '24px', width: '24px' }} />}
          <Lottie
            loop
            animationData={animationData}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              zIndex: 1,
              pointerEvents: 'none',
              transition: 'opacity 0.3s ease-in-out',
              transformOrigin: 'center center',
              transform: 'translate3d(-18px, -18px, 0)',
              width: '80px',
              height: '80px',
              opacity: isQueryRequestSent ? 1 : 0,
              display: isQueryRequestSent ? 'block' : 'none',
            }}
          />
        </LogoContainer>
        <div style={{ width: '100%' }}>
          <Content data-automation="CopilotContent">
            <TextAreaWrapper isActive hasBorder={false} data-automation="CopilotTextAreaWrapper">
              <TextAreaWrapperInner isActive hasBorder={false}>
                <Textarea
                  data-automation="CopilotTextarea"
                  id="copilot-textarea"
                  inputRef={messageInputRef}
                  value={queryMessage}
                  placeholder={placeHolderText}
                  multiline
                  minRows={1}
                  maxRows={6}
                  fullWidth
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleUpdateMessage(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={isQueryRequestSent}
                />
                {queryMessage.length === 0 && (
                  <FakePlaceholder>
                    <span>{placeHolderText}</span>
                  </FakePlaceholder>
                )}
              </TextAreaWrapperInner>
            </TextAreaWrapper>
            <PromptSupportCopyText>{promptCopy}</PromptSupportCopyText>
            {maxCharsExceeded && (
              <MaxCharacterContainerRow data-automation="MaxCharacterContainerRow">
                {maxCharsExceeded && (
                  <CharacterCounter id="messageCharCounter" charCount={queryMessage.length} maxChars={maxChars} />
                )}
                {maxCharsExceeded && (
                  <MaxCharMessageWrapper data-automation="MaxCharMessageWrapper">
                    <MaxCharMessage data-automation="MaxCharMessage">
                      Remove <strong>{charsOverBy}</strong> {charsOverBy > 1 ? 'characters' : 'character'}
                    </MaxCharMessage>
                  </MaxCharMessageWrapper>
                )}
              </MaxCharacterContainerRow>
            )}
          </Content>
          {shouldShowReferralButton && (
            <div style={{ display: 'flex', alignItems: 'center', marginTop: '8px', gap: '8px' }}>
              <Button
                size="small"
                state="secondary"
                id="GenerateReferralSummaryButton"
                onClick={() => handleClickGenerateSummary(PromptType.REFERRAL, 'Referral Summary')}
                data-automation="GenerateReferralSummaryButton"
                style={{ height: '28px', padding: '0 8px', fontSize: '14px' }}
                disabled={isQueryRequestSent}
              >
                {referralButtonText}
              </Button>
              <Button
                size="small"
                state="secondary"
                id="GeneratePatientSummaryButton"
                onClick={() => handleClickGenerateSummary(PromptType.PATIENT, 'Patient-Facing Summary')}
                data-automation="GeneratePatientSummaryButton"
                style={{ height: '28px', padding: '0 8px', fontSize: '14px' }}
                disabled={isQueryRequestSent}
              >
                Generate patient-facing summary
              </Button>
            </div>
          )}
        </div>
        <Button
          size="small"
          state="primary"
          id="AskCopilotButton"
          onClick={handleClickAskCopilotButton}
          disabled={isDisabled}
          data-automation="AskCopilotButton"
          style={{ height: '42px', padding: '0 16px', fontSize: '16px', marginTop: '3px' }}
        >
          Ask
        </Button>
      </CopilotContainer>
    </StyledPaper>
  );
};
